import { Link, useRouterState } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  Bot,
  Banknote,
  ContactRound,
  ClipboardList,
  Gavel,
  History,
  LandPlot,
  LayoutGrid,
  Leaf,
  MessagesSquare,
  SlidersHorizontal,
  Sprout,
  Target,
  User,
  UsersRound,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { fetchCurrentUser } from '@/lib/auth';
import { useUIStore } from '@/stores/ui-store';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  // Quando definido, item só aparece se o user tem essa área (admin vê tudo).
  area?: string;
  // Quando true, só admin vê o item (independente de área).
  adminOnly?: boolean;
  // Quando true, somente gestores e administradores veem o item.
  managerOnly?: boolean;
}

interface NavGroup {
  title: string;
  items: NavItem[];
}

const NAV: NavGroup[] = [
  {
    title: 'Geral',
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: LayoutGrid },
      // Chat com os agentes IA — transversal (todos veem; a lista interna
      // mostra os agentes relevantes). Distinto de /agents (admin OpenClaw).
      { to: '/agentes', label: 'Assistentes IA', icon: MessagesSquare },
    ],
  },
  {
    title: 'Negócios',
    items: [
      // Clientes é transversal (todos veem). Demais módulos seguem áreas.
      { to: '/clientes', label: 'Clientes', icon: ContactRound },
      { to: '/crm', label: 'CRM', icon: Target, area: 'crm' },
      { to: '/ambiental', label: 'Ambiental', icon: Leaf, area: 'ambiental' },
      { to: '/credito', label: 'Crédito Rural', icon: Banknote, area: 'credito_rural' },
      { to: '/geo', label: 'Fundiário', icon: LandPlot, area: 'fundiario' },
      { to: '/agronomia', label: 'Agronomia', icon: Sprout },
      { to: '/juridico', label: 'Jurídico', icon: Gavel, area: 'juridico' },
      { to: '/gestao', label: 'Gestão', icon: ClipboardList, managerOnly: true },
    ],
  },
  {
    title: 'Histórico',
    items: [{ to: '/history', label: 'Decisões', icon: History, adminOnly: true }],
  },
  {
    title: 'Sistema',
    items: [
      // Módulo Agentes (gestão) — Visão geral/Autoaprendizado/Custo. A página
      // antiga de cards (/agents) foi aposentada; redireciona pra cá.
      { to: '/agentes/visao-geral', label: 'Agentes', icon: Bot, adminOnly: true },
      { to: '/skills', label: 'Skills', icon: Zap, adminOnly: true },
    ],
  },
  {
    title: 'Admin',
    items: [
      { to: '/usuarios', label: 'Usuários', icon: UsersRound, adminOnly: true },
      { to: '/config', label: 'Configurações', icon: SlidersHorizontal, adminOnly: true },
      // Perfil é público — todo usuário gerencia o próprio.
      { to: '/perfil', label: 'Meu Perfil', icon: User },
    ],
  },
];

const isElectron = typeof window !== 'undefined' && 'electronBridge' in window;

// Marca ARA — sulcos de arado (3 linhas curvas) no squircle coral.
function AraLogo() {
  return (
    <span className="flex size-7 shrink-0 items-center justify-center rounded-[9px] bg-accent">
      <svg width="16" height="16" viewBox="0 0 20 20" fill="none" aria-hidden="true">
        <path
          d="M2.2 6.2C7 3.2 13 3.2 17.8 6.2M2.2 11.4C7 8.4 13 8.4 17.8 11.4M2.2 16.6C7 13.6 13 13.6 17.8 16.6"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

export function Sidebar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { mobileSidebarOpen, closeMobileSidebar } = useUIStore();
  const { data: me } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => fetchCurrentUser(signal),
    staleTime: 5 * 60_000,
  });

  // Admin vê tudo. Outros usuários veem:
  //   - itens públicos (sem area, sem adminOnly): Dashboard, Clientes, Perfil
  //   - itens com area: só se tiverem aquela área em user_area
  //   - itens adminOnly: nunca
  const isAdmin = me?.role === 'admin';
  const userAreaCodes = new Set((me?.areas ?? []).map((a) => a.codigo));
  const canSee = (item: NavItem) => {
    if (isAdmin) return true;
    if (item.adminOnly) return false;
    if (item.managerOnly) return me?.role === 'gestor';
    if (item.area) return userAreaCodes.has(item.area);
    return true;
  };

  // Filtra grupos vazios (depois do filtro de itens) — evita renderizar
  // "Negócios" sem nenhum link, por exemplo.
  const visibleNav = NAV.map((g) => ({
    ...g,
    items: g.items.filter(canSee),
  })).filter((g) => g.items.length > 0);

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-40 flex flex-col',
        // Safe-area: no app (edge-to-edge) o drawer não pode encostar na status bar.
        'pt-[env(safe-area-inset-top)]',
        'w-[var(--sidebar-w)] border-r border-border bg-[var(--sidebar-bg)]',
        'transition-transform duration-200 ease-out',
        'md:relative md:translate-x-0 md:shrink-0',
        mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full',
      )}
    >
      <div
        id="sidebar-brand"
        className={cn(
          'px-4 flex items-center gap-2.5 border-b border-border',
          isElectron ? 'pt-10 pb-2.5' : 'h-[var(--topbar-h)]',
        )}
      >
        <AraLogo />
        <span className="text-[17px] font-bold tracking-tight text-text">ARA</span>
      </div>
      <nav className="scroll-auto-hide flex-1 overflow-y-auto px-3 py-[18px] flex flex-col gap-5">
        {visibleNav.map((group) => (
          <div key={group.title}>
            {/* Menu ARA: labels de seção 11px/500, tracking 1px, uppercase. */}
            <div className="px-3 pb-1.5 text-[11px] font-medium uppercase tracking-[1px] text-text-2 opacity-85">
              {group.title}
            </div>
            <ul className="flex flex-col gap-[3px]">
              {group.items.map((item) => {
                const active = pathname === item.to;
                const Icon = item.icon;
                return (
                  <li key={item.to}>
                    {/* Item pill r12; ativo = par M3 (container + conteúdo coral),
                        tokens --nav-active-* variam por tema (dark: 700 + 100). */}
                    <Link
                      to={item.to}
                      onClick={closeMobileSidebar}
                      className={cn(
                        'flex items-center gap-[11px] rounded-[12px] text-[0.875rem]',
                        'px-3 py-[8px]',
                        'transition-colors',
                        active
                          ? 'bg-[var(--nav-active-bg)] font-medium text-[var(--nav-active-fg)]'
                          : 'font-medium text-[var(--sidebar-txt)] hover:bg-[var(--nav-hover-bg)] hover:text-text',
                      )}
                    >
                      <Icon size={18} strokeWidth={1.6} className="shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </nav>
      {/* Rodapé com a versão do app — útil pra suporte e bug reports. */}
      <div className="px-4 py-2 border-t border-border text-[0.65rem] text-text-muted flex items-center justify-between">
        <span>ARA · Ruralplan</span>
        <span className="font-mono tabular-nums">v{__APP_VERSION__}</span>
      </div>
    </aside>
  );
}
